// Shared domain types — the contract between the frontend, Cloud Functions,
// and Firestore documents. Functions has its own copy of the wire-level
// interfaces in functions/src/types.ts (kept in sync by hand; the two
// workspaces compile separately). Timestamps cross the callable boundary as
// ISO strings; Firestore Timestamps only exist server-side.

export type Role = 'employee' | 'supervisor' | 'director' | 'admin'
export type EmployeeStatus = 'needs_setup' | 'active' | 'inactive'
export type WeekStatus = 'draft' | 'publishing' | 'published'
export type ShiftStatus = 'assigned' | 'open' | 'cancelled'
export type RequestType = 'swap' | 'drop' | 'claim' | 'overtime' | 'timeoff'
export type RequestState =
  | 'pending_peer'
  | 'pending_approval'
  | 'pending_overtime'
  | 'approved'
  | 'denied'
  | 'cancelled'
  | 'superseded'
  | 'expired'

export type Weekday = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'
export const WEEKDAYS: Weekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

/** Workspace-synced identity fields — overwritten hourly by syncRoster, never edited in-app. */
export interface WorkspaceProfile {
  displayName: string
  givenName: string
  familyName: string
  title: string | null
  workspaceDepartment: string | null
  phone: string | null
  mobilePhone: string | null
  managerEmail: string | null
  badgeNumber: string | null
  photoUrl: string | null
  paylocityEntity: string | null
  syncedAt: string
}

export interface Employee {
  email: string
  workspace: WorkspaceProfile
  departmentId: string | null
  homeTeamId: string | null
  positionIds: string[]
  role: Role
  supervisorTeamIds: string[]
  status: EmployeeStatus
  otThresholdHours: number | null
  notificationPrefs: { email: boolean; openShiftBroadcasts: boolean }
  lastSeenAt: string | null
  createdAt: string
  updatedAt: string
}

export interface Department {
  id: string
  name: string
  active: boolean
  calendarId: string | null
  otThresholdHours: number
  createdAt: string
  updatedAt: string
}

export interface Team {
  id: string
  departmentId: string
  name: string
  sortOrder: number
  active: boolean
}

export interface Position {
  id: string
  departmentId: string
  name: string
  sortOrder: number
  requiredCredentialTypeIds: string[]
  color: string | null
  active: boolean
}

export interface CredentialType {
  id: string
  departmentId: string | null
  name: string
  warnDaysBefore: number
  active: boolean
}

export interface Credential {
  id: string
  employeeEmail: string
  departmentId: string
  typeId: string
  typeName: string
  identifier: string | null
  issuedAt: string | null
  expiresAt: string
  notes: string | null
  createdBy: string
  updatedAt: string
}

export interface WeekEvent {
  date: string
  title: string
  details: string
}

export interface Week {
  id: string
  departmentId: string
  weekStart: string
  status: WeekStatus
  publishedAt: string | null
  publishedBy: string | null
  publishVersion: number
  events: WeekEvent[]
  createdFrom: { kind: 'blank' | 'template' | 'copy'; sourceId: string | null }
  createdAt: string
  updatedAt: string
}

export interface Shift {
  id: string
  departmentId: string
  weekId: string
  teamId: string
  positionId: string
  positionName: string
  /** Local start day, YYYY-MM-DD (America/Detroit). weekId is derived from this. */
  date: string
  /** Local wall-clock HH:mm. */
  startTime: string
  endTime: string
  endsNextDay: boolean
  /** True UTC instants (ISO). Source of truth for overlap checks and duration. */
  startAt: string
  endAt: string
  breakMinutes: number
  durationHours: number
  status: ShiftStatus
  assigneeEmail: string | null
  assigneeName: string | null
  published: boolean
  notes: string | null
  ackedAt: string | null
  ackResetAt: string | null
  ackResetReason: string | null
  version: number
  pendingRequestId: string | null
  claimCount: number
  needsCredentialReview: boolean
  calendarEventId: string | null
  calendarHash: string | null
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
}

export interface ShiftRequest {
  id: string
  type: RequestType
  state: RequestState
  departmentId: string
  teamId: string | null
  requesterEmail: string
  requesterName: string
  createdAt: string
  expiresAt: string | null
  decidedAt: string | null
  decidedBy: string | null
  decisionNote: string | null
  requiresOvertimeApproval: boolean
  otRequestId: string | null
  // swap / claim / drop
  shiftId?: string
  shiftVersion?: number
  counterpartyEmail?: string
  counterpartyName?: string
  counterpartyShiftId?: string
  counterpartyShiftVersion?: number
  peerDecidedAt?: string | null
  reason?: string
  resolution?: { kind: 'open' } | { kind: 'reassign'; email: string }
  // overtime
  subjectEmail?: string
  subjectName?: string
  weekId?: string
  projectedHours?: number
  thresholdHours?: number
  parentRequestId?: string | null
  // timeoff
  startDate?: string
  endDate?: string
  partial?: { startTime: string; endTime: string } | null
  conflictShiftIds?: string[]
}

export interface Availability {
  email: string
  departmentId: string
  weekly: Record<Weekday, { start: string; end: string }[]>
  notes: string | null
  updatedAt: string
  updatedBy: string
}

export interface TemplateShift {
  teamId: string
  positionId: string
  dayOfWeek: number // 0 = Sunday
  startTime: string
  endTime: string
  endsNextDay: boolean
  breakMinutes: number
  assigneeEmail: string | null
  notes: string | null
}

export interface WeekTemplate {
  id: string
  departmentId: string
  name: string
  description: string | null
  shifts: TemplateShift[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface ApprovalCounters {
  departmentId: string
  swaps: number
  drops: number
  claims: number
  timeoff: number
  overtime: number
  needsSetup: number
  updatedAt: string
}

export interface AuditEntry {
  id: string
  action: string
  actor: string
  actorName: string
  at: string
  departmentId: string | null
  subject: { type: string; id: string; name: string | null }
  details: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// Capabilities (role → what the UI may show / routes may allow).
// Scope (own team / own department) is enforced server-side; `can()` only
// gates surfaces.
// ---------------------------------------------------------------------------

export type Capability =
  | 'view_own'
  | 'view_team'
  | 'request_shift_change'
  | 'claim_open'
  | 'edit_availability'
  | 'request_time_off'
  | 'build_schedule'
  | 'approve_requests'
  | 'publish_schedule'
  | 'approve_ot'
  | 'manage_roster'
  | 'manage_credentials'
  | 'manage_templates'
  | 'manage_department'
  | 'manage_departments'
  | 'view_audit'
  | 'manage_global'

const EMPLOYEE_CAPS: Capability[] = [
  'view_own',
  'view_team',
  'request_shift_change',
  'claim_open',
  'edit_availability',
  'request_time_off',
]
const SUPERVISOR_CAPS: Capability[] = [...EMPLOYEE_CAPS, 'build_schedule', 'approve_requests']
const DIRECTOR_CAPS: Capability[] = [
  ...SUPERVISOR_CAPS,
  'publish_schedule',
  'approve_ot',
  'manage_roster',
  'manage_credentials',
  'manage_templates',
  'manage_department',
]
const ADMIN_CAPS: Capability[] = [...DIRECTOR_CAPS, 'manage_departments', 'view_audit', 'manage_global']

export const CAPABILITIES: Record<Role, Capability[]> = {
  employee: EMPLOYEE_CAPS,
  supervisor: SUPERVISOR_CAPS,
  director: DIRECTOR_CAPS,
  admin: ADMIN_CAPS,
}
